export const ODDS_MARKET_KEYS = new Set([
  "oneXTwo",
  "doubleChance",
  "bothTeamsToScore",
  "overUnderGoals",
  "halfTimeFullTime",
  "oneXTwoAndOverUnder",
  "correctScore",
  "cleanSheet",
  "totalGoalsExact",
  "oddEven",
  "teamToScore",
  "halfWithMoreGoals",
  "winToNil",
  "drawNoBet",
  "doubleChanceAndBothTeamsToScore",
]);

export const ODDS_FIELD_LABELS = {
  oneXTwo: "Match Result",
  firstHalf: "First Half",
  secondHalf: "Second Half",
  fullTime: "Full Time",
  doubleChance: "Double Chance",
  bothTeamsToScore: "Both Teams to Score",
  overUnderGoals: "Over/Under Goals",
  halfTimeFullTime: "Half Time/Full Time",
  oneXTwoAndOverUnder: "Match Result & Over/Under",
  correctScore: "Correct Score",
  cleanSheet: "Clean Sheet",
  totalGoalsExact: "Total Goals Exact",
  oddEven: "Odd/Even",
  teamToScore: "Team to Score",
  halfWithMoreGoals: "Half with More Goals",
  winToNil: "Win to Nil",
  drawNoBet: "Draw No Bet",
  doubleChanceAndBothTeamsToScore: "Double Chance & Both Teams to Score",
  homeTeam: "Home",
  awayTeam: "Away",
  yes: "Yes",
  no: "No",
  odd: "Odd",
  even: "Even",
  noGoal: "No goal",
  bothTeams: "Both teams",
  equal: "Equal",
  "1": "Home Win",
  X: "Draw",
  "2": "Away Win",
  "1X": "1X",
  X2: "X2",
  12: "12",
};

export const formatOutcomeLabel = (key) => {
  if (ODDS_FIELD_LABELS[key]) return ODDS_FIELD_LABELS[key];
  const overMatch = String(key).match(/^over(\d)_(\d)$/);
  if (overMatch) return `Over ${overMatch[1]}.${overMatch[2]}`;
  const underMatch = String(key).match(/^under(\d)_(\d)$/);
  if (underMatch) return `Under ${underMatch[1]}.${underMatch[2]}`;
  return String(key).replace(/_/g, " ").replace(/\//g, " / ");
};

export const isEmptyOddsValue = (value) => {
  if (value === "" || value === null || value === undefined) return true;
  if (typeof value === "number" && value === 0) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
};

export const validateOddsValue = (value) => {
  if (isEmptyOddsValue(value)) {
    return { valid: true, normalizedValue: null, message: null };
  }
  const normalizedValue = String(value).replace(",", ".");
  const parsedValue = parseFloat(normalizedValue);

  if (Number.isNaN(parsedValue)) {
    return { valid: false, message: "Invalid number", normalizedValue: null };
  }
  if (parsedValue < 1.01) {
    return { valid: false, message: "Minimum odds: 1.01", normalizedValue: parsedValue };
  }
  const decimalPlaces = (normalizedValue.split(".")[1] || "").length;
  if (decimalPlaces > 2) {
    return { valid: false, message: "Max 2 decimal places", normalizedValue: parsedValue };
  }
  return { valid: true, message: null, normalizedValue: parsedValue };
};

export const collectValidationErrors = (odds) => {
  const errors = {};
  const walk = (obj, path) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = path ? `${path}.${key}` : key;
      if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        walk(value, currentPath);
      } else if (typeof value === "string" || typeof value === "number") {
        const validation = validateOddsValue(value);
        if (!validation.valid) errors[currentPath] = validation.message;
      }
    }
  };

  if (!odds || typeof odds !== "object") return errors;
  for (const marketKey of Object.keys(odds)) {
    if (ODDS_MARKET_KEYS.has(marketKey) && odds[marketKey] && typeof odds[marketKey] === "object") {
      walk(odds[marketKey], marketKey);
    }
  }
  return errors;
};

export const formatOddsInput = (value) => {
  if (value === null || value === undefined) return "";
  let cleaned = String(value).replace(/[^0-9.,]/g, "");
  const commaIndex = cleaned.lastIndexOf(",");
  const dotIndex = cleaned.lastIndexOf(".");
  if (commaIndex > -1 && dotIndex > -1) {
    if (commaIndex > dotIndex) {
      cleaned = cleaned.substring(0, dotIndex) + cleaned.substring(dotIndex + 1);
    } else {
      cleaned = cleaned.substring(0, commaIndex) + cleaned.substring(commaIndex + 1);
    }
  }
  const parts = cleaned.split(/[,.]/);
  if (parts.length > 1 && parts[1].length > 2) {
    cleaned = `${parts[0]}${cleaned.includes(",") ? "," : "."}${parts[1].substring(0, 2)}`;
  }
  return cleaned;
};

export const formatOddsDisplayValue = (value) => {
  if (isEmptyOddsValue(value)) return "";
  return String(value);
};

export const deepMergeOdds = (target, source) => {
  if (!source || typeof source !== "object") return source;
  const result = Array.isArray(target) ? [...target] : { ...target };
  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    const targetValue = result[key];
    if (
      sourceValue &&
      typeof sourceValue === "object" &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === "object" &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMergeOdds(targetValue, sourceValue);
    } else {
      result[key] = sourceValue;
    }
  }
  return result;
};

const walkOddsLeaves = (obj, visit) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
  for (const value of Object.values(obj)) {
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      walkOddsLeaves(value, visit);
    } else {
      visit(value);
    }
  }
};

export const hasAnyFilledOddsInput = (odds) => {
  if (!odds || typeof odds !== "object") return false;
  for (const marketKey of Object.keys(odds)) {
    if (!ODDS_MARKET_KEYS.has(marketKey)) continue;
    const section = odds[marketKey];
    if (!section || typeof section !== "object") continue;
    let found = false;
    walkOddsLeaves(section, (value) => {
      if (!isEmptyOddsValue(value)) found = true;
    });
    if (found) return true;
  }
  return false;
};

export const countOddsSummary = (odds) => {
  const stats = { markets: 0, filled: 0 };
  if (!odds || typeof odds !== "object") return stats;
  for (const marketKey of Object.keys(odds)) {
    if (!ODDS_MARKET_KEYS.has(marketKey)) continue;
    const section = odds[marketKey];
    if (!section || typeof section !== "object") continue;
    walkOddsLeaves(section, (value) => {
      stats.markets += 1;
      if (!isEmptyOddsValue(value)) stats.filled += 1;
    });
  }
  return stats;
};

export const sectionHasOddsValues = (obj) => {
  if (!obj || typeof obj !== "object") return false;
  for (const value of Object.values(obj)) {
    if (typeof value === "number" && value > 0) return true;
    if (typeof value === "object" && sectionHasOddsValues(value)) return true;
  }
  return false;
};

export const listOddsMarketSections = (odds = {}) =>
  Object.entries(odds).filter(
    ([sectionTitle, sectionData]) =>
      ODDS_MARKET_KEYS.has(sectionTitle) &&
      sectionData &&
      typeof sectionData === "object"
  );

export const updateOddsAtPath = (odds, path, updater) => {
  const levels = path.split(".");
  const lastKey = levels.pop();
  if (!levels.length) {
    return { ...odds, [lastKey]: updater(odds?.[lastKey]) };
  }

  const setAtDepth = (node, depth) => {
    const key = levels[depth];
    if (depth === levels.length - 1) {
      return {
        ...node,
        [key]: {
          ...(node?.[key] || {}),
          [lastKey]: updater(node?.[key]?.[lastKey]),
        },
      };
    }
    return {
      ...node,
      [key]: setAtDepth(node?.[key] || {}, depth + 1),
    };
  };

  return setAtDepth(odds, 0);
};

export const isLeafOddsValue = (value) =>
  typeof value === "string" || typeof value === "number" || value === null;

export const EMPTY_ODDS_FEED = { success: true, hasOdds: false, bookmakers: [] };

export const isMissingOddsFeedMessage = (message) =>
  /no odds available|no external odds feed/i.test(String(message || ""));

export const normalizeOddsFeedError = (error, fallback = "Failed to load odds feeds") => {
  const status = error?.response?.status;
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.errors?.[0] ||
    error?.message ||
    "";
  if (status === 404 && isMissingOddsFeedMessage(message)) return null;
  return message || fallback;
};
